import React from 'react';
import {useTranslation} from 'react-i18next';
import {CaretRight, EyeSlash} from '../../icons';
import {ClientAvatar, ClientPickerButton} from './ClientPicker';
import {AccessCell, AccountIdentity, LimitCell, ListFooter} from './AccountParts';

function EmptyLine({text}) {
    return <p className="aac-empty">{text}</p>;
}

/**
 * El cartel de ayuda del repo (`warning-tooltip`), no el `title` nativo: el de la fila es la única
 * explicación de un ícono suelto o de la sugerencia, y el `title` sólo sale con el mouse quieto
 * (review C-11). Abre también con el foco del teclado (`:focus-within`, en el SCSS).
 */
function Tip({text, children, align = '-below-right'}) {
    return (
        <span className="info-icon-container aac-tip">
            {children}
            <span className={`warning-tooltip ${align}`} role="tooltip">
                <span className="warning-tooltip-body">
                    <span className="warning-tooltip-text">{text}</span>
                </span>
            </span>
        </span>
    );
}

/**
 * «Conectadas a clientes»: agrupadas por cliente, con Cuenta · Moneda · Límite diario · Acceso · ›.
 * El nombre y el chevron abren el detalle.
 */
export function LinkedView({page, shown, total, emptyText, onMore, onOpen, onReconnect}) {
    const {t} = useTranslation();
    return (
        <div className="aac-card" data-testid="aac-linked">
            <div className="aac-thead aac-grid -linked" aria-hidden="true">
                <span>{t('agency_v2.accounts.col.account')}</span>
                <span>{t('agency_v2.accounts.col.currency')}</span>
                <span>{t('agency_v2.accounts.col.daily_limit')}</span>
                <span>{t('agency_v2.accounts.col.access')}</span>
                <span/>
            </div>
            {!total && <EmptyLine text={emptyText}/>}
            {page.map((group) => (
                <section key={group.key} className="aac-group" aria-label={group.clientName}>
                    <div className="aac-ghead">
                        <ClientAvatar name={group.clientName}/>
                        <b className="aac-ghead__name">{group.clientName}</b>
                        <span className="aac-ghead__n">{t('agency_v2.accounts.group_count', {count: group.total})}</span>
                    </div>
                    {group.rows.map((row) => (
                        <div key={row.key} className="aac-row aac-grid -linked" data-testid={`aac-row-${row.key}`}>
                            <AccountIdentity row={row} onOpen={() => onOpen(row)}/>
                            <span className="aac-cell" data-label={t('agency_v2.accounts.col.currency')}>
                                <span className="aac-muted">{(row.raw && row.raw.currency_code) || '—'}</span>
                            </span>
                            <span className="aac-cell" data-label={t('agency_v2.accounts.col.daily_limit')}>
                                <LimitCell row={row}/>
                            </span>
                            <span className="aac-cell" data-label={t('agency_v2.accounts.col.access')}>
                                <AccessCell row={row} onReconnect={onReconnect}/>
                            </span>
                            <button type="button" className="aac-chev" aria-label={t('agency_v2.accounts.open_detail')}
                                    onClick={() => onOpen(row)}>
                                <CaretRight size={16} weight="bold" aria-hidden="true"/>
                            </button>
                        </div>
                    ))}
                </section>
            ))}
            {total > 0 && <ListFooter shown={shown} total={total} onMore={onMore}/>}
        </div>
    );
}

/**
 * «Sin cliente»: agrupadas por actividad, cada una con su tilde, «Conectar a un cliente ▾» (el
 * buscador, con la sugerencia por nombre) e «Ignorar».
 */
export function UnlinkedView({page, shown, total, emptyText, selected, busyKeys, locked = false, suggestions,
                                 pickerProps, onToggle, onConnect, onCreateClient, onIgnore, onMore}) {
    const {t} = useTranslation();
    return (
        <div className="aac-card" data-testid="aac-unlinked">
            {!total && <EmptyLine text={emptyText}/>}
            {page.map((group) => (
                <section key={group.key} className="aac-ugroup">
                    {group.group && (
                        <h3 className="aac-ushead">
                            {t(`agency_v2.accounts.unlinked_group.${group.group}`, {count: group.total})}
                        </h3>
                    )}
                    {group.rows.map((row) => {
                        const isSelected = selected.has(row.key);
                        // Mientras corre un lote, ninguna fila acepta otro gesto (review C-05).
                        const busy = locked || busyKeys.has(row.key);
                        const suggested = suggestions[row.key] || null;
                        return (
                            <div key={row.key} className={`aac-urow aac-grid -unlinked${isSelected ? ' -sel' : ''}`}
                                 data-testid={`aac-row-${row.key}`}>
                                <input type="checkbox" className="aac-cb" checked={isSelected} disabled={busy}
                                       aria-label={t('agency_v2.accounts.select')}
                                       onChange={() => onToggle(row.key)}/>
                                <AccountIdentity row={row}/>
                                <span className="aac-cell -hint">
                                    {suggested && (
                                        <Tip text={t('agency_v2.accounts.picker_suggested_why')} align="-below">
                                            <span className="aac-sug" tabIndex={0}>
                                                {t('agency_v2.accounts.picker_suggested')}
                                                <ClientAvatar name={suggested.name}/>
                                                <span className="aac-sug__name">{suggested.name}</span>
                                            </span>
                                        </Tip>
                                    )}
                                </span>
                                <span className="aac-uact">
                                    <ClientPickerButton {...pickerProps} label={t('agency_v2.accounts.connect_to_client')}
                                                        suggested={suggested} busy={busy}
                                                        onPick={(client) => onConnect(row, client)}
                                                        onCreate={() => onCreateClient(row)}/>
                                    <Tip text={t('agency_v2.accounts.ignore')}>
                                        <button type="button" className="aac-icon-btn" disabled={busy}
                                                aria-label={t('agency_v2.accounts.ignore')}
                                                onClick={() => onIgnore(row)}>
                                            <EyeSlash size={16} weight="bold" aria-hidden="true"/>
                                        </button>
                                    </Tip>
                                </span>
                            </div>
                        );
                    })}
                </section>
            ))}
            {total > 0 && <ListFooter shown={shown} total={total} onMore={onMore}/>}
        </div>
    );
}

function ignoredWho(t, row) {
    if (row.snoozedBy && row.snoozedOn) {
        return t('agency_v2.accounts.ignored_by', {user: row.snoozedBy, date: row.snoozedOn, interpolation: {escapeValue: false}});
    }
    if (row.snoozedOn) {
        return t('agency_v2.accounts.ignored_on', {date: row.snoozedOn, interpolation: {escapeValue: false}});
    }
    return '';
}

/** «Ignoradas»: quién y cuándo, y «Conectar a un cliente». No hay «Volver a mostrar» (D10). */
export function IgnoredView({page, shown, total, emptyText, busyKeys, locked = false, pickerProps, onConnect,
                                onCreateClient, onMore}) {
    const {t} = useTranslation();
    const rows = page.reduce((all, group) => all.concat(group.rows), []);
    return (
        <div className="aac-card" data-testid="aac-ignored">
            {!total && <EmptyLine text={emptyText}/>}
            {rows.map((row) => (
                <div key={row.key} className="aac-urow aac-grid -ignored" data-testid={`aac-row-${row.key}`}>
                    <AccountIdentity row={row}/>
                    <span className="aac-cell -hint aac-muted">{ignoredWho(t, row)}</span>
                    <span className="aac-uact">
                        <ClientPickerButton {...pickerProps} label={t('agency_v2.accounts.connect_to_client')}
                                            suggested={null} busy={locked || busyKeys.has(row.key)}
                                            onPick={(client) => onConnect(row, client)}
                                            onCreate={() => onCreateClient(row)}/>
                    </span>
                </div>
            ))}
            {total > 0 && (
                <ListFooter shown={shown} total={total} onMore={onMore}/>
            )}
            <p className="aac-foot-note">{t('agency_v2.accounts.ignored_footer')}</p>
        </div>
    );
}
